import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { calculateDiscountedAmount } from "@/components/Checkout";
import { ApplyCouponModal } from "@/components/Checkout/ApplyCouponModal";
import Image from "@/components/common/Image";
import useSnackbar, { defaultSnackbarProps, IconWithSnackbarMessage, Snackbar } from "@/components/common/Snackbar";
import Icon from "@/components/icon/Icon";
import { NoPoint, Sizes } from "@/components/icon/IconList";
import { numberFormatter, priceFormatter } from "@/lib/utils";
import { DEFAULT_BOOK_COVER } from "@/settings/constant";
import { Button, Divider, Typography } from "@bookips/sds";
import { Empty } from "antd";
import * as React from "react";
import { useCallback, useState } from "react";
import useCheckoutStore from "../../../store/checkout.store";
export const CheckoutListEmpty = (param)=>{
    let { title , emptyOptions , onEmptyClick  } = param;
    const handleEmptyClick = useCallback(()=>{
        if (onEmptyClick) {
            onEmptyClick();
        }
    }, [
        onEmptyClick
    ]);
    return /*#__PURE__*/ _jsx(Empty, {
        ...emptyOptions,
        description: /*#__PURE__*/ _jsx("span", {
            children: title
        }),
        children: /*#__PURE__*/ _jsx(Button, {
            onClick: handleEmptyClick,
            children: "새로 만들기"
        })
    });
};
export const CheckoutListItem = (param)=>{
    let { item , applyCoupon , checkAvailableCouponList , userCoupons , isUpgrade  } = param;
    // store에서 캐시/포인트 사용 금액 구독
    const { usedCash , usedPoint  } = useCheckoutStore((state)=>({
            usedCash: state.priceInfo.usedCash || 0,
            usedPoint: state.priceInfo.usedPoint || 0
        }));
    const getThumbImg = ()=>{
        if (!item.productThumbImg) {
            return DEFAULT_BOOK_COVER;
        }
        if (item.productThumbImg.includes("%")) {
            return item.productThumbImg;
        }
        return encodeURI(item.productThumbImg);
    };
    const thumbImg = getThumbImg();
    const availableCouponList = checkAvailableCouponList(item.productId);
    const [isShowApplyCouponModal, setIsShowApplyCouponModal] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("적용된 할인이 변경되었어요.");
    const showApplyCouponModal = useCallback(()=>{
        setIsShowApplyCouponModal(true);
    }, []);
    const { isShowSnackbar , showSnackbar , hideSnackbar  } = useSnackbar();
    // 쿠폰 적용 시 캐시/포인트 리셋 여부에 따라 메시지 변경
    const handleApplyCouponWithMessage = (currentItem, coupon)=>{
        const hadCashOrPoint = usedCash > 0 || usedPoint > 0;
        applyCoupon(currentItem, coupon);
        setSnackbarMessage(hadCashOrPoint ? "쿠폰 적용으로 캐시/포인트가 초기화됐어요." : "적용된 할인이 변경되었어요.");
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex gap-5",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "relative w-[68px] h-[96px] min-h-0 max-h-[96px] rounded-sm overflow-hidden flex-shrink-0 border border-gray-300/20 box-border",
                children: /*#__PURE__*/ _jsx(Image, {
                    src: thumbImg,
                    alt: "library-detail-bg",
                    fill: true
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-3 pb-[1px]",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-1",
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "body1",
                                children: item.productTitle
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "body2",
                                className: "text-secondary",
                                children: item.productProvider
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                variant: "caption",
                                className: "text-secondary",
                                children: [
                                    "구매옵션 : ",
                                    item.purchaseOption === "view" ? "열람+출력 (대여)" : "PDF 다운 (소장)"
                                ]
                            })
                        ]
                    }),
                    !isUpgrade && /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-1",
                        children: [
                            item.isCouponApplied && /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-col gap-1",
                                children: /*#__PURE__*/ _jsxs(Typography, {
                                    variant: "subtitle1",
                                    className: "flex gap-1.5 items-center",
                                    children: [
                                        priceFormatter(item.discountedPrice),
                                        "원",
                                        /*#__PURE__*/ _jsxs(Typography, {
                                            variant: "caption",
                                            className: "text-muted text line-through",
                                            children: [
                                                priceFormatter(item.price),
                                                "원"
                                            ]
                                        })
                                    ]
                                })
                            }),
                            item.isCouponApplied === false && /*#__PURE__*/ _jsxs(Typography, {
                                variant: "subtitle1",
                                className: "flex gap-1.5 items-center",
                                children: [
                                    priceFormatter(item.price),
                                    "원"
                                ]
                            }),
                            item.isCouponApplied && /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2 items-center",
                                children: [
                                    /*#__PURE__*/ _jsx(Button, {
                                        onClick: showApplyCouponModal,
                                        variant: "stroke",
                                        size: "sm",
                                        color: "secondary",
                                        className: "text-primary rounded-sm",
                                        children: "쿠폰 변경"
                                    }),
                                    /*#__PURE__*/ _jsxs(Typography, {
                                        variant: "caption",
                                        className: "text-red-400",
                                        children: [
                                            numberFormatter(calculateDiscountedAmount({
                                                price: item.price,
                                                discount: item.discountedPrice
                                            })),
                                            "원 할인 적용"
                                        ]
                                    })
                                ]
                            }),
                            !item.isCouponApplied && availableCouponList.length > 0 && /*#__PURE__*/ _jsx(Button, {
                                onClick: showApplyCouponModal,
                                size: "sm",
                                className: "bg-red-400 w-fit rounded-sm",
                                children: "쿠폰 사용"
                            })
                        ]
                    }),
                    item.isPointUnusable && /*#__PURE__*/ _jsxs(Typography, {
                        variant: "caption",
                        className: "text-secondary flex items-center gap-1",
                        children: [
                            /*#__PURE__*/ _jsx(Icon, {
                                icon: NoPoint,
                                size: Sizes.small,
                                className: "text-muted w-4 h-4"
                            }),
                            "포인트 사용 불가 상품"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ApplyCouponModal, {
                showSnackbar: showSnackbar,
                applyCoupon: handleApplyCouponWithMessage,
                item: item,
                isOpen: isShowApplyCouponModal,
                couponList: availableCouponList,
                onClose: ()=>setIsShowApplyCouponModal(false)
            }),
            /*#__PURE__*/ _jsx(Snackbar, {
                ...defaultSnackbarProps,
                open: isShowSnackbar,
                onClose: hideSnackbar,
                message: /*#__PURE__*/ _jsx(IconWithSnackbarMessage, {
                    text: snackbarMessage
                })
            })
        ]
    });
};
export const CheckoutList = (param)=>{
    let { userCoupons , dataSource =[] , appliedMaxCoupon , applyCoupon , checkAvailableCouponList , isUpgrade  } = param;
    if (dataSource.length <= 0) {
        return /*#__PURE__*/ _jsx(CheckoutListEmpty, {
            title: "표시할 데이터가 없습니다.",
            emptyOptions: {
                image: "https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg",
                imageStyle: {
                    height: 60
                }
            }
        });
    }
    return /*#__PURE__*/ _jsx("ul", {
        className: "flex flex-col gap-5",
        children: dataSource.map((item, idx)=>/*#__PURE__*/ _jsxs("li", {
                className: "flex flex-col gap-5",
                children: [
                    /*#__PURE__*/ _jsx(CheckoutListItem, {
                        item: item,
                        userCoupons: userCoupons,
                        applyCoupon: applyCoupon,
                        checkAvailableCouponList: checkAvailableCouponList,
                        isUpgrade: isUpgrade
                    }),
                    idx !== dataSource.length - 1 && /*#__PURE__*/ _jsx(Divider, {})
                ]
            }, idx))
    });
};
